import React, { useState, useEffect } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import axios from '../../config/axios.js';
import Alert from '../common/Alert.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import salaryService from '../../services/SalaryService.js';
import axiosInstance from '../../config/axios.js';
import { Wallet, Plus } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { useTranslation } from 'react-i18next';
import { listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';
import { downloadBlob } from '../../utils/downloadBlob.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';


const SalaryList = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const canManage = ['System Admin', 'Admin', 'Accountant'].includes(currentUser?.role);
  const isAgent = currentUser?.role === 'Agent';
  const [salaries, setSalaries] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState('current_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const navigate = useNavigate();
  const perPage = 10;

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  useEffect(() => {
    fetchSalaries();
  }, [currentPage, statusFilter, userFilter, dateRangeFilter, customStartDate, customEndDate]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuId && !e.target.closest('.menu-container')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users?all=true');
      setUsers(response.data.data || []);
    } catch (err) {
      console.error('Failed to load users', err);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get('/roles?all=true');
      setRoles(response.data.data || []);
    } catch (err) {
      console.error('Failed to load roles', err);
    }
    };
    
    const handleDownloadSalarySlip = async (salary) => {
      try {
        const month = (salary.month || salary.month_year?.split(' ')?.[0] || '').toLowerCase();
        const year = salary.year || salary.month_year?.split(' ')?.[1];
        if (!month || !year) {
          Alert.error('Unable to determine salary period for download');
          return;
        }

        const blob = await salaryService.downloadSalarySlipFile(salary.user_id, month, year);
        const saved = await downloadBlob(
          blob,
          `salary_slip_${salary.user_id}_${month}_${year}.pdf`,
          'application/pdf',
        );
        if (saved?.method === 'cancelled') return;
        Alert.success('Salary slip downloaded successfully!');
      } catch (err) {
        Alert.error(err.response?.data?.error || err.message || 'Failed to download salary slip');
        console.error('Download error:', err);
      }
    };

  const getDateRange = () => {
    const currentDate = new Date();
    let startDate, endDate;

    switch (dateRangeFilter) {
      case 'current_month':
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        endDate = currentDate;
        break;
      case 'last_month':
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
        break;
      case 'last_3_months':
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 3, 1);
        endDate = currentDate;
        break;
      case 'last_6_months':
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 6, 1);
        endDate = currentDate;
        break;
      case 'last_year':
        startDate = new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), 1);
        endDate = currentDate;
        break;
      case 'all':
        return { startDate: null, endDate: null };
      case 'custom':
        if (customStartDate && customEndDate) {
          return {
            startDate: new Date(customStartDate),
            endDate: new Date(customEndDate)
          };
        }
        return { startDate: null, endDate: null };
      default:
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        endDate = currentDate;
    }

    return { startDate, endDate };
  };

  const fetchSalaries = async () => {
    try {
      setLoading(true);
      const filters = {};
      
      if (statusFilter) filters.status = statusFilter;
      if (userFilter) filters.user_id = userFilter;

      // Get date range based on filter
      const { startDate, endDate } = getDateRange();

      const response = await axiosInstance.get('/salaries', {
        params: {
          page: currentPage,
          per_page: perPage,
          ...filters,
          created_after: startDate ? startDate.toISOString() : undefined,
          created_before: endDate ? endDate.toISOString() : undefined
        }
      });

      const salariesData = response.data.data || [];
      setSalaries((prev) => mergeLoadedPage(currentPage, salariesData, prev));
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (err) {
      Alert.error('Failed to load salary records');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (salaryId, userName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete salary record for "${userName}"?`,
      'Delete Salary Record'
    );

    if (confirmed) {
      try {
        await salaryService.delete(salaryId);
        Alert.success('Salary record deleted successfully!');
        
        if (salaries.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchSalaries();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete salary record');
      }
    }
  };

  const toggleMenu = (salaryId) => {
    setOpenMenuId(openMenuId === salaryId ? null : salaryId);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(Math.round(amount || 0));
  };

  const getStatusColor = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      credited: 'bg-green-100 text-green-800 border-green-200',
      processing: 'bg-blue-100 text-blue-800 border-blue-200',
      hold: 'bg-orange-100 text-orange-800 border-orange-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.role_name : 'N/A';
  };

  const clearFilters = () => {
    setStatusFilter('');
    setUserFilter('');
    setDateRangeFilter('current_month');
    setCustomStartDate('');
    setCustomEndDate('');
    setCurrentPage(1);
  };

  const hasActiveFilters = statusFilter || userFilter || dateRangeFilter !== 'current_month' || (dateRangeFilter === 'custom' && (customStartDate || customEndDate));

  const getDateRangeLabel = () => {
    const labels = {
      current_month: 'Current Month',
      last_month: 'Last Month',
      last_3_months: 'Last 3 Months',
      last_6_months: 'Last 6 Months',
      last_year: 'Last Year',
      all: 'All Time',
      custom: 'Custom Range'
    };
    return labels[dateRangeFilter] || 'Current Month';
  };

  return (
    <DashboardLayout>
      <ListPageHeader
        title="Salary Records"
        subtitle="Manage employee salary and payroll"
        icon={Wallet}
        titleClassName="text-primary"
        iconClassName="w-6 h-6 sm:w-7 sm:h-7 shrink-0"
        className="mb-4 sm:mb-6"
      >
        {canManage && (
          <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/salaries/add')}>
            Create Salary
          </Button>
        )}
      </ListPageHeader>

      <ListFilterPanel
        hasActiveFilters={hasActiveFilters}
        onClearFilters={clearFilters}
        gridClassName={`grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-4 ${!isAgent ? 'xl:grid-cols-4' : 'xl:grid-cols-3'}`}
      >
        {!isAgent && (
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">User</label>
            <select
              value={userFilter}
              onChange={(e) => {
                setUserFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
            >
              <option value="">All Users</option>
              {users.map((user) => (
                <option key={user.user_id} value={user.user_id}>
                  {user.name} ({getRoleName(user.role_id)}) - {user.user_id}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="credited">Credited</option>
            <option value="processing">Processing</option>
            <option value="hold">Hold</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Date Range</label>
          <select
            value={dateRangeFilter}
            onChange={(e) => {
              setDateRangeFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
          >
            <option value="current_month">Current Month</option>
            <option value="last_month">Last Month</option>
            <option value="last_3_months">Last 3 Months</option>
            <option value="last_6_months">Last 6 Months</option>
            <option value="last_year">Last Year</option>
            <option value="all">All Time</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>

        {dateRangeFilter === 'custom' && (
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Start Date</label>
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => {
                setCustomStartDate(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
            />
          </div>
        )}

        {dateRangeFilter === 'custom' && (
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">End Date</label>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => {
                setCustomEndDate(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
            />
          </div>
        )}
      </ListFilterPanel>

      <div className="bg-white rounded-xl shadow-sm">
        {loading && currentPage === 1 ? (
          <div className="flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message="Loading salaries..." />
          </div>
        ) : salaries.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="text-5xl sm:text-6xl mb-4">💰</div>
            <p className="text-gray-600 text-base sm:text-lg font-semibold">No salary records found</p>
            <p className="text-gray-500 mt-2 text-sm">Try adjusting your filters for {getDateRangeLabel()}</p>
          </div>
        ) : (
          <>
            {/* Desktop / large tablet table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 xl:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Salary ID</th>
                    <th className="px-4 xl:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">User</th>
                    <th className="px-4 xl:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Period</th>
                    <th className="px-4 xl:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Basic Salary</th>
                    <th className="px-4 xl:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Deductions</th>
                    <th className="px-4 xl:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Net Salary</th>
                    <th className="px-4 xl:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                    <th className="px-4 xl:px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {salaries.map((salary) => (
                    <tr key={salary.salary_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 xl:px-6 py-4 text-sm">
                        <button
                          type="button"
                          onClick={() => navigate(`/salaries/view/${salary.salary_id}`)}
                          className={listIdLinkClass}
                        >
                          {salary.salary_id}
                        </button>
                      </td>
                      <td className="px-4 xl:px-6 py-4 text-sm">
                        <div className="font-medium text-gray-900">{salary.user_details?.name}</div>
                        <div className="text-xs text-gray-500">
                          {getRoleName(salary.user_details?.role_id)} • {salary.user_details?.user_id}
                        </div>
                        {salary.user_details?.city && (
                          <div className="text-xs text-gray-400 mt-0.5">{salary.user_details.city}</div>
                        )}
                      </td>
                      <td className="px-4 xl:px-6 py-4 text-sm text-gray-900">{salary.month_year}</td>
                      <td className="px-4 xl:px-6 py-4 text-sm text-gray-900">{formatCurrency(salary.basic_salary)}</td>
                      <td className="px-4 xl:px-6 py-4 text-sm text-gray-900">{formatCurrency(salary.deductions)}</td>
                      <td className="px-4 xl:px-6 py-4 text-sm font-semibold text-gray-900">{formatCurrency(salary.net_salary)}</td>
                      <td className="px-4 xl:px-6 py-4 text-sm">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold uppercase border ${getStatusColor(salary.status)}`}>
                          {salary.status}
                        </span>
                      </td>
                      <td className="px-4 xl:px-6 py-4 text-sm text-center">
                        <RowActionsMenu
                          className="relative menu-container"
                          isOpen={openMenuId === salary.salary_id}
                          onToggle={() => toggleMenu(salary.salary_id)}
                          onClose={() => setOpenMenuId(null)}
                          onView={() => navigate(`/salaries/view/${salary.salary_id}`)}
                          onEdit={canManage ? () => navigate(`/salaries/edit/${salary.salary_id}`) : undefined}
                          onDelete={canManage ? () => handleDelete(salary.salary_id, salary.user_details?.name) : undefined}
                          extraItems={[
                            {
                              label: 'Download Slip',
                              icon: '📄',
                              onClick: () => handleDownloadSalarySlip(salary),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile & iPad card view */}
            <div className="lg:hidden divide-y divide-gray-100">
              {salaries.map((salary) => (
                <div key={salary.salary_id} className="p-4 sm:p-5 border-l-4 border-l-blue-500">
                  <div className="flex justify-between items-start gap-3 mb-3">
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => navigate(`/salaries/view/${salary.salary_id}`)}
                        className={listIdLinkMobileClass}
                      >
                        {salary.salary_id}
                      </button>
                      <p className="text-xs text-gray-500 mt-0.5">{salary.month_year}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-bold uppercase border ${getStatusColor(salary.status)}`}>
                        {salary.status}
                      </span>
                      <RowActionsMenu
                        className="relative menu-container"
                        isOpen={openMenuId === salary.salary_id}
                        onToggle={() => toggleMenu(salary.salary_id)}
                        onClose={() => setOpenMenuId(null)}
                        onView={() => navigate(`/salaries/view/${salary.salary_id}`)}
                        onEdit={canManage ? () => navigate(`/salaries/edit/${salary.salary_id}`) : undefined}
                        onDelete={canManage ? () => handleDelete(salary.salary_id, salary.user_details?.name) : undefined}
                        extraItems={[
                          {
                            label: 'Download Slip',
                            icon: '📄',
                            onClick: () => handleDownloadSalarySlip(salary),
                          },
                        ]}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                    <div>
                      <p className="text-xs text-gray-500 font-medium">User</p>
                      <p className="font-semibold text-gray-800 truncate">{salary.user_details?.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Basic Salary</p>
                      <p className="font-bold text-gray-900">{formatCurrency(salary.basic_salary)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Deductions</p>
                      <p className="font-bold text-gray-900">{formatCurrency(salary.deductions)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Net Salary</p>
                      <p className="font-bold text-green-700">{formatCurrency(salary.net_salary)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <ListPagination
                                  currentPage={currentPage}
                                  totalPages={totalPages}
                                  loading={loading && currentPage > 1}
                                  onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
                                />
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SalaryList;